import React, { useState } from "react";
import api from "@/lib/api";
import { fmtUSD } from "./utils";

// FX converter — shows the EUR totals in USD using either the rate the agent
// has explicitly saved on this itinerary (preferred) or the daily ECB rate.
//
// The rate is editable; clicking "Auto" replaces it with today's live ECB
// value AND clears the persisted override so future visits also use the
// live feed. Manual edits persist via `onPersist` so they survive close/open.
export function FxConverter({ fx, setFx, totals, onPersist }) {
  const [busy, setBusy] = useState(false);
  // "Saved" means the agent has committed a real rate to the itinerary
  // doc — either by typing a value into the input OR by clicking "Guardar
  // esta tasa" after Auto. A source of "manual" alone isn't enough: if
  // they cleared the input mid-edit, `rate` is 0 while source is still
  // "manual", and the backend would fall back to the cached ECB rate. So
  // we require BOTH signals to hide the warning.
  const isSaved = fx.source === "manual" && Number(fx.rate) > 0;
  const onChangeRate = (v) => {
    const n = parseFloat(v);
    const next = { ...fx, rate: Number.isFinite(n) && n >= 0 ? n : 0, source: "manual" };
    setFx(next);
    if (onPersist) onPersist(next.rate);  // save to the itinerary doc
  };
  const saveCurrentRate = () => {
    // One-click "freeze this rate" — persists whatever the widget is showing
    // right now (typically today's ECB, freshly fetched by Auto). Keeps the
    // decision in the agent's hands while making the save as low-friction
    // as typing into the input.
    const rate = Number(fx.rate) || 0;
    if (rate <= 0) return;
    setFx({ ...fx, source: "manual" });
    if (onPersist) onPersist(rate);
  };
  const reload = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/fx/rate", { params: { refresh: true } });
      if (data?.rate) {
        setFx({ rate: Number(data.rate), source: data.source, date: data.date });
        // Auto is *informational*: it shows today's ECB rate but the DECISION
        // to freeze it onto the itinerary stays with the agent — they click
        // "Guardar esta tasa" (or type into the input) to persist. No amount
        // mismatch either way: `_fx_rate_for` on the backend falls back to
        // the same cached ECB feed when `fx_rate` is null.
        if (onPersist) onPersist(null);
      }
    } catch (_e) {
      // FX refresh is best-effort; we keep showing the previously cached rate.
    } finally {
      setBusy(false);
    }
  };
  const pvpUsd = (totals.pvp || 0) * (fx.rate || 0);
  const sourceLabel = fx.source === "fresh" ? "ECB (hoy)"
    : fx.source === "cache" ? "ECB (cache)"
    : fx.source === "stale" ? "ECB (último guardado)"
    : fx.source === "manual" ? "Manual (guardado)"
    : fx.source === "fallback" ? "Fallback 1.10" : "Cargando…";
  return (
    <div className="mt-3 border border-clay-300 bg-clay-50/50 p-3" data-testid="fx-converter">
      <div className="flex items-center justify-between text-[11px] text-clay-700 mb-2">
        <span className="smallcaps">Conversión EUR → USD</span>
        <span className="text-clay-600">{sourceLabel}{fx.date ? ` · ${fx.date}` : ""}</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-clay-700">1 € =</span>
        <input
          data-testid="fx-rate-input"
          type="number"
          step="0.01"
          value={Number(fx.rate || 0).toFixed(2)}
          onChange={(e) => onChangeRate(e.target.value)}
          className="w-24 bg-white border border-clay-300 px-1 py-0.5 text-sm tabular text-right"
        />
        <span className="text-xs text-clay-700">USD</span>
        <button
          data-testid="fx-refresh"
          onClick={reload}
          disabled={busy}
          className="ml-auto text-[10px] uppercase tracking-wider border border-clay-400 px-2 py-1 hover:bg-clay-900 hover:text-white transition disabled:opacity-50"
          title="Consultar la tasa ECB de hoy (informativo)"
        >
          {busy ? "…" : "Auto"}
        </button>
      </div>
      {!isSaved && fx.source !== "loading" && (
        <div
          data-testid="fx-not-saved-warning"
          className="mb-2 flex items-start gap-2 border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
        >
          <span aria-hidden="true" className="mt-[1px] shrink-0">⚠</span>
          <div className="flex-1">
            <div className="font-medium">Tasa no guardada en este itinerario</div>
            <div className="text-amber-800">
              Guárdala para fijar el precio del enlace de pago y evitar que cambie si la tasa se mueve.
            </div>
          </div>
          <button
            data-testid="fx-save-rate"
            onClick={saveCurrentRate}
            className="shrink-0 text-[10px] uppercase tracking-wider border border-amber-700 bg-amber-700 text-white px-2 py-1 hover:bg-amber-800 transition"
            title="Fijar esta tasa en el itinerario"
          >
            Guardar
          </button>
        </div>
      )}
      <div className="flex items-center justify-between py-2 bg-pine text-white px-3" data-testid="pvp-usd">
        <div className="smallcaps text-white/70">PVP en USD</div>
        <div className="font-serif text-2xl tabular">{fmtUSD(pvpUsd)}</div>
      </div>
    </div>
  );
}
